import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INSURERS = [
  "Allianz",
  "Česká podnikatelská pojišťovna (ČPP)",
  "ČSOB Pojišťovna",
  "Direct pojišťovna",
  "Generali Česká pojišťovna",
  "Kooperativa",
  "Pillow pojišťovna",
  "Slavia pojišťovna",
  "Triglav pojišťovna",
  "UNIQA",
  "Jiná / nevím",
];

const BENEFITS = [
  "Včasné upozornění před výročím pojištění",
  "Individuální porovnání nabídek pojišťoven",
  "Nezávazná konzultace zdarma",
];

export const PovinneRuceni = () => {
  const mountedAt = useRef(Date.now());
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ name: "", email: "", phone: "", period_start: "", insurer: "", note: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (!form.period_start) next.period_start = "Vyberte prosím datum.";
    else if (form.period_start <= new Date().toISOString().slice(0, 10)) next.period_start = "Zadejte prosím budoucí datum.";
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/povinne-ruceni`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - mountedAt.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === "ok" || data.status === "demo")) {
        setStatus("sent");
      } else {
        setStatus("idle");
        const detail = data.detail;
        setServerError(
          (typeof detail === "string" && detail) || "Poptávku se nepodařilo odeslat. Zkuste to prosím znovu."
        );
      }
    } catch {
      setStatus("idle");
      setServerError("Poptávku se nepodařilo odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  const inputCls = (err) =>
    `bg-white border ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-4 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

  const labelCls = "block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2";

  return (
    <section id="povinne-ruceni" data-testid="povinne-ruceni-section" className="py-24 md:py-32 border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal className="flex flex-col">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Hlídání povinného ručení
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6">
            Platíte za pojištění auta víc, než musíte?
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-6">
            Blíží se výročí vašeho povinného ručení? Nechte si včas porovnat nabídky
            pojišťoven. Pohlídám správný termín a ozvu se vám s možnostmi, které
            budou na trhu aktuální.
          </p>

          <ul className="mt-8 space-y-4">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-4">
                <Check className="h-5 w-5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-base text-[#252525]">{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="relative mt-10 flex-1 min-h-[260px] lg:min-h-0 w-full max-w-md lg:max-w-none">
            <img
              src="/images/povinne-ruceni.webp"
              alt="Pojištění vozidla — hlídání výročí povinného ručení"
              width={1200}
              height={685}
              loading="lazy"
              data-testid="povinne-ruceni-image"
              className="absolute inset-0 w-full h-full object-cover block rounded-tl-[80px] rounded-2xl"
            />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-[#F0F0F0] bg-[#FAFAF8] p-8 md:p-10">
            <h3 className="font-display text-2xl text-[#252525]" data-testid="pr-form-title">
              Pohlídat moje povinné ručení
            </h3>

            {status === "sent" ? (
              <div role="status" data-testid="pr-success" className="flex items-start gap-4 mt-8 border border-[#F0F0F0] bg-white p-6">
                <Check className="h-6 w-6 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                <p className="text-base text-[#252525] leading-relaxed">
                  Děkuji! Termín vašeho povinného ručení mám zaznamenaný. Včas se vám
                  ozvu a společně se podíváme, zda pro vás existuje výhodnější řešení.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="pr-form" className="mt-8 space-y-6">
                <div>
                  <label htmlFor="pr-date" className={labelCls}>
                    Začátek dalšího pojistného období *
                  </label>
                  <input
                    id="pr-date"
                    data-testid="pr-date-input"
                    type="date"
                    min={tomorrow}
                    value={form.period_start}
                    onChange={set("period_start")}
                    className={inputCls(errors.period_start)}
                    aria-invalid={!!errors.period_start}
                  />
                  <p className="text-xs text-[#737373] mt-2">
                    Najdete v pojistné smlouvě nebo na výpisu — jde o první den nového období.
                  </p>
                  {errors.period_start && <p role="alert" data-testid="pr-date-error" className="text-sm text-red-700 mt-2">{errors.period_start}</p>}
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="pr-name" className={labelCls}>Jméno *</label>
                    <input
                      id="pr-name"
                      data-testid="pr-name-input"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={set("name")}
                      className={inputCls(errors.name)}
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <p role="alert" data-testid="pr-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="pr-email" className={labelCls}>E-mail *</label>
                    <input
                      id="pr-email"
                      data-testid="pr-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputCls(errors.email)}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <p role="alert" data-testid="pr-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="pr-phone" className={labelCls}>
                      Telefon <span className="normal-case font-normal">(nepovinné — pro rychlejší kontakt)</span>
                    </label>
                    <input
                      id="pr-phone"
                      data-testid="pr-phone-input"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls(false)}
                    />
                  </div>
                  <div>
                    <label htmlFor="pr-insurer" className={labelCls}>
                      Současná pojišťovna <span className="normal-case font-normal">(nepovinné)</span>
                    </label>
                    <select
                      id="pr-insurer"
                      data-testid="pr-insurer-select"
                      value={form.insurer}
                      onChange={set("insurer")}
                      className={`${inputCls(false)} ${form.insurer ? "text-[#252525]" : "text-[#A3A3A3]"}`}
                    >
                      <option value="">Vyberte…</option>
                      {INSURERS.map((insurer) => (
                        <option key={insurer} value={insurer}>{insurer}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="pr-note" className={labelCls}>
                    Poznámka <span className="normal-case font-normal">(nepovinné)</span>
                  </label>
                  <textarea
                    id="pr-note"
                    data-testid="pr-note-input"
                    rows={3}
                    value={form.note}
                    onChange={set("note")}
                    placeholder="Např. jestli řešíte i havarijní pojištění…"
                    className={`${inputCls(false)} resize-y`}
                  />
                </div>

                <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="pr-website">Webové stránky</label>
                  <input
                    id="pr-website"
                    data-testid="pr-honeypot"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set("website")}
                  />
                </div>

                {serverError && <p role="alert" data-testid="pr-server-error" className="text-sm text-red-700">{serverError}</p>}

                <button
                  type="submit"
                  data-testid="pr-submit-button"
                  disabled={status === "sending"}
                  className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-widest font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Odesílám…" : "Pohlídat termín a porovnat nabídky"}
                </button>

                <p className="text-xs text-[#737373] leading-relaxed">
                  Odeslání formuláře je nezávazné a neznamená uzavření pojistné smlouvy.
                  Odesláním souhlasíte se zpracováním uvedených údajů pro vyřízení
                  poptávky a následné kontaktování ve zvoleném termínu.{" "}
                  <Link to="/ochrana-osobnich-udaju" data-testid="pr-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
                    Informace o zpracování osobních údajů
                  </Link>
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
